/**
 * LinguistPro — PayFast onsite payment lifecycle (2026-09-23)
 *
 * Wraps `window.payfast_do_onsite_payment` so the one caller (PaymentModal)
 * shares one implementation of:
 * focusing the iframe once PayFast's window opens, refusing a second
 * concurrent payment, resolving to a single outcome even when PayFast's
 * callback is silent (CSP block, dropped postMessage, ad-blocker,
 * third-party cookie block), mapping that outcome to UI state, and loading
 * PayFast's script with a working retry after a failed load.
 *
 * Everything PayFast/DOM-shaped is injectable so this can be unit-tested in
 * vitest's `node` environment (no jsdom in this repo).
 */

export const PAYFAST_CONTAINER_ID = 'payfast_div_container';
export const PAYFAST_IFRAME_ID = 'payfast_compact_engine_iframe';

/** How long we wait for PayFast's onsite callback before giving up and
 *  surfacing a retry path. Defence in depth against any silent-callback
 *  failure — register key
 *  linguistpro-top-up-payments-for-customer-ab339ae9-are-failing-at-payfast-itn-all. */
export const ONSITE_CALLBACK_TIMEOUT_MS = 100_000;

/** Thrown (and matched by the modal) when a second window would open over the first. */
export const PAYFAST_ALREADY_OPEN_MESSAGE = 'A payment window is already open. Finish or close it first.';

export type OnsiteOutcome = 'paid' | 'cancelled' | 'incomplete';
export type PaymentPlan = 'payg' | 'pro' | 'gift';

/**
 * Copy per Marketing Guru ruling 5b8a421b (2026-09-23): PayFast's
 * callback(false) only means its window closed — for methods that settle
 * later, money can still move, and PayFast's server notification (ITN) is
 * the proof, not this callback. So neither "cancelled" nor "incomplete" may
 * claim nothing happened; both point the customer at their balance/wallet
 * and warn against a double payment. The two outcome keys stay distinct in
 * code (for later telemetry) even though today their text is identical.
 */
export const ONSITE_MESSAGES: Record<PaymentPlan, Record<'cancelled' | 'incomplete', string>> = {
  payg: {
    cancelled:
      "We couldn't confirm your payment. If money left your account, your balance updates within seconds. Check your balance before retrying to avoid paying twice.",
    incomplete:
      "We couldn't confirm your payment. If money left your account, your balance updates within seconds. Check your balance before retrying to avoid paying twice.",
  },
  pro: {
    cancelled:
      "We couldn't confirm your payment. If money left your account, your balance updates within seconds. Check your balance before retrying to avoid paying twice.",
    incomplete:
      "We couldn't confirm your payment. If money left your account, your balance updates within seconds. Check your balance before retrying to avoid paying twice.",
  },
  gift: {
    cancelled:
      "We couldn't confirm your payment. If money left your account, your gift card appears in your Wallet within seconds. Check your Wallet before retrying to avoid paying twice.",
    incomplete:
      "We couldn't confirm your payment. If money left your account, your gift card appears in your Wallet within seconds. Check your Wallet before retrying to avoid paying twice.",
  },
};

/** Success-box copy. Gift purchases credit a redemption code, not the
 *  balance, so — unlike payg/pro — the gift text must not promise a
 *  balance update. */
export function successMessage(plan: PaymentPlan): string {
  return plan === 'gift'
    ? 'Payment received! Your gift card code will appear in your Wallet → Gift Cards shortly.'
    : 'Payment received! Your balance is being updated...';
}

export interface OnsiteUiState {
  loading: false;
  success: boolean;
  notice: string | null;
}

/**
 * Single source of truth mapping a payment outcome + plan to UI state, used
 * by PaymentModal so the outcome mapping cannot drift (e.g. forgetting
 * to clear loading on a cancel, leaving "Processing..." forever).
 */
export function outcomeToUi(outcome: OnsiteOutcome, plan: PaymentPlan): OnsiteUiState {
  if (outcome === 'paid') {
    return { loading: false, success: true, notice: null };
  }
  return { loading: false, success: false, notice: ONSITE_MESSAGES[plan][outcome] };
}

export function isPayFastWindowOpen(doc: Pick<Document, 'getElementById'> = document): boolean {
  return doc.getElementById(PAYFAST_CONTAINER_ID) != null;
}

export interface StartOnsitePaymentOptions {
  uuid: string;
  onOutcome: (outcome: OnsiteOutcome) => void;
  onWindowOpen?: () => void;
  win?: { payfast_do_onsite_payment: Window['payfast_do_onsite_payment'] };
  doc?: Pick<Document, 'getElementById'>;
  /** Milliseconds before a silent window is reported 'incomplete'; null leaves the timeout to the caller. */
  timeoutMs?: number | null;
  pollMs?: number;
  setTimer?: typeof setTimeout;
  clearTimer?: typeof clearTimeout;
  setIntervalFn?: typeof setInterval;
  clearIntervalFn?: typeof clearInterval;
}

/**
 * Opens PayFast's onsite modal and resolves to exactly one of 'paid' /
 * 'cancelled' / 'incomplete' — with the real callback always taking priority
 * over our own timeout/poll-based 'incomplete' guess, since a customer can
 * finish paying after we've already given up waiting.
 *
 * PayFast keeps one global finish callback (`window.payfast_do_finish_callback`),
 * so starting a second payment while one is already open strands the first
 * container and hides our own windows until a reload. Refuse up front.
 *
 * Returns a stop function that cancels all pending timers (call it on
 * unmount or before starting a new attempt).
 */
export function startOnsitePayment(opts: StartOnsitePaymentOptions): () => void {
  const {
    uuid,
    onOutcome,
    onWindowOpen,
    win = window,
    doc = document,
    timeoutMs = ONSITE_CALLBACK_TIMEOUT_MS,
    pollMs = 500,
    setTimer = setTimeout,
    clearTimer = clearTimeout,
    setIntervalFn = setInterval,
    clearIntervalFn = clearInterval,
  } = opts;

  if (isPayFastWindowOpen(doc)) {
    throw new Error(PAYFAST_ALREADY_OPEN_MESSAGE);
  }

  // True once the real PayFast callback has fired (paid or cancelled) or stop()
  // was called — terminal, blocks everything further, including a second
  // callback invocation.
  let done = false;
  // True once we've emitted our own 'incomplete' guess (via poll or timeout).
  // Non-terminal: the real callback can still fire afterwards and win.
  let incompleteEmitted = false;
  let sawContainer = false;

  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  let pollHandle: ReturnType<typeof setInterval> | null = null;

  function stopTimers() {
    if (timeoutHandle !== null) {
      clearTimer(timeoutHandle);
      timeoutHandle = null;
    }
    if (pollHandle !== null) {
      clearIntervalFn(pollHandle);
      pollHandle = null;
    }
  }

  function handleCallback(result: boolean) {
    if (done) return;
    done = true;
    stopTimers();
    onOutcome(result === true ? 'paid' : 'cancelled');
  }

  win.payfast_do_onsite_payment({ uuid }, handleCallback);

  if (isPayFastWindowOpen(doc)) {
    sawContainer = true;
    onWindowOpen?.();
    const iframe = doc.getElementById(PAYFAST_IFRAME_ID) as HTMLElement | null;
    iframe?.focus?.();
  }

  pollHandle = setIntervalFn(() => {
    if (done) return;
    const containerNow = isPayFastWindowOpen(doc);
    if (containerNow) {
      sawContainer = true;
      return;
    }
    if (sawContainer && !incompleteEmitted) {
      incompleteEmitted = true;
      onOutcome('incomplete');
      stopTimers();
    }
  }, pollMs);

  if (timeoutMs !== null) {
    timeoutHandle = setTimer(() => {
      timeoutHandle = null;
      if (done || incompleteEmitted) return;
      if (isPayFastWindowOpen(doc)) {
        // Customer may still be typing card details inside PayFast's window —
        // never remove or hide their container from our code, just keep waiting.
        return;
      }
      incompleteEmitted = true;
      onOutcome('incomplete');
      if (pollHandle !== null) {
        clearIntervalFn(pollHandle);
        pollHandle = null;
      }
    }, timeoutMs);
  }

  return () => {
    done = true;
    stopTimers();
  };
}

export interface ScheduleFollowUpsOptions {
  refresh: () => void;
  close?: () => void;
  delays?: number[];
  closeAfterMs?: number;
  setTimer?: typeof setTimeout;
  clearTimer?: typeof clearTimeout;
}

export interface FollowUpsHandle {
  /** Cancels only the auto-close, leaving balance refreshes running. */
  cancelClose: () => void;
  /** Cancels the auto-close and every pending balance refresh. */
  cancelAll: () => void;
}

/**
 * Schedules balance refreshes independently of an optional auto-close, and
 * returns cancel handles: `cancelClose` for the auto-close alone (a retry
 * must never cancel a paid attempt's refreshes), `cancelAll` for unmount or
 * a superseding outcome. Independence matters: AppLayout's success path
 * auto-closes at 4s, but the 5/10/15/20s refreshes must keep firing after
 * the modal is gone (SPEC-023's original bug: closing cancelled the whole
 * polling effect via its cleanup, so a customer could close on a stale
 * balance). Used for both success (with closeAfterMs) and cancelled/
 * incomplete (without it, since the copy promises the balance/wallet still
 * updates on its own).
 */
export function scheduleFollowUps({
  refresh,
  close,
  delays = [2000, 5000, 10000, 15000, 20000],
  closeAfterMs,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
}: ScheduleFollowUpsOptions): FollowUpsHandle {
  const refreshHandles: ReturnType<typeof setTimeout>[] = delays.map((ms) => setTimer(() => refresh(), ms));
  let closeHandle: ReturnType<typeof setTimeout> | null = null;
  if (closeAfterMs !== undefined && close) {
    closeHandle = setTimer(() => close(), closeAfterMs);
  }
  const cancelClose = () => {
    if (closeHandle !== null) {
      clearTimer(closeHandle);
      closeHandle = null;
    }
  };
  return {
    cancelClose,
    cancelAll: () => {
      cancelClose();
      refreshHandles.forEach((h) => clearTimer(h));
    },
  };
}

interface ScriptLikeElement {
  id: string;
  src: string;
  onload: (() => void) | null;
  onerror: (() => void) | null;
  remove(): void;
}

export interface LoadPayFastScriptDoc {
  getElementById(id: string): ScriptLikeElement | null;
  createElement(tagName: string): ScriptLikeElement;
  head: { appendChild(node: ScriptLikeElement): void };
}

export interface LoadPayFastScriptWin {
  payfast_do_onsite_payment?: unknown;
}

const PAYFAST_SCRIPT_ID = 'payfast-onsite-js';
const PAYFAST_SCRIPT_SRC = 'https://www.payfast.co.za/onsite/engine.js';

/**
 * Loads PayFast's onsite engine script, injectable for testing. A failed
 * load previously left the dead `<script id="payfast-onsite-js">` tag in the
 * page, so every later attempt resolved immediately (the tag "exists") and
 * threw "not a function" until a full reload. Now: remove the tag on error,
 * and treat an existing tag whose script never actually defined
 * `payfast_do_onsite_payment` as dead too — remove and reload.
 */
export function loadPayFastScript(
  doc: LoadPayFastScriptDoc = document as unknown as LoadPayFastScriptDoc,
  win: LoadPayFastScriptWin = window as unknown as LoadPayFastScriptWin
): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = doc.getElementById(PAYFAST_SCRIPT_ID);
    if (existing) {
      if (typeof win.payfast_do_onsite_payment === 'function') {
        resolve();
        return;
      }
      existing.remove();
    }
    const script = doc.createElement('script');
    script.id = PAYFAST_SCRIPT_ID;
    script.src = PAYFAST_SCRIPT_SRC;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error('Failed to load PayFast script'));
    };
    doc.head.appendChild(script);
  });
}

/**
 * Observes `doc.body` for PayFast's container being added/removed and
 * reports open/closed via `onChange` — extracted from usePayFastWindowOpen
 * so it can be unit-tested without a real MutationObserver/DOM. Reports
 * once immediately after `observe()` so a caller polling initial state
 * (e.g. a hook's effect) sees the current value right away, not just future
 * mutations.
 */
export function watchPayFastWindow(
  doc: Pick<Document, 'getElementById' | 'body'>,
  MutationObserverCtor: typeof MutationObserver,
  onChange: (open: boolean) => void
): () => void {
  const observer = new MutationObserverCtor(() => {
    onChange(isPayFastWindowOpen(doc));
  });
  observer.observe(doc.body, { childList: true });
  onChange(isPayFastWindowOpen(doc));
  return () => observer.disconnect();
}
